export const useOnboarding = () => {
  const supabase = useSupabaseClient();
  const user = useSupabaseUser();

  /**
   * Check if user should see the onboarding form
   * Returns true if:
   * 1. User is authenticated
   * 2. User hasn't completed the onboarding form
   */
  const shouldShowOnboardingForm = async () => {
    if (!user.value) {
      return false;
    }

    try {
      // Check if user profile exists and if onboarding form was completed
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('onboarding_form_completed, created_at, updated_at')
        .eq('user_id', user.value.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 is "not found" error, which is expected for new users
        return false;
      }

      // If no profile exists, this is a new user - show the form
      if (!profile) {
        return true;
      }

      const isCompleted = (profile as any).onboarding_form_completed;

      // MOST IMPORTANT: If form is completed, NEVER show the form again
      if (isCompleted === true) {
        return false;
      }

      // If profile exists but form not completed, show the form
      return true;

    } catch (err) {
      return false;
    }
  };

  /**
   * Mark onboarding form as completed
   * SIMPLIFIED VERSION - No RPC function dependency
   */
  const markOnboardingCompleted = async () => {
    if (!user.value) {
      return false;
    }

    try {
      
      // Direct upsert approach - more reliable than RPC
      const { data: upsertData, error: upsertError } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: user.value.id,
          onboarding_form_completed: true,
          created_at: new Date().toISOString(), // Will be ignored on update due to conflict
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        })
        .select('onboarding_form_completed');

      if (upsertError) {
        return false;
      }
      
      // Wait a moment for the database to process
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Verify the update by checking the profile
      const { data: profile, error: checkError } = await supabase
        .from('user_profiles')
        .select('onboarding_form_completed, updated_at')
        .eq('user_id', user.value.id)
        .single();
        
      if (checkError) {
        // Don't fail if verification fails, assume success since upsert worked
        return true;
      }
      
      const finalStatus = (profile as any).onboarding_form_completed;
      
      return finalStatus === true;
      
    } catch (err) {
      return false;
    }
  };

  /**
   * Create initial user profile (ONLY if it doesn't exist)
   * SIMPLIFIED VERSION - No RPC function dependency
   */
  const createUserProfile = async () => {
    if (!user.value) return false;

    try {      
      // First check if profile already exists
      const { data: existingProfile, error: checkError } = await supabase
        .from('user_profiles')
        .select('onboarding_form_completed, id')
        .eq('user_id', user.value.id)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        return false;
      }

      // If profile already exists, DON'T overwrite it!
      if (existingProfile) {
        return true;
      }
      
      // Create new profile with upsert (safer than insert)
      const { data, error } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: user.value.id,
          onboarding_form_completed: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        })
        .select();

      if (error) {
        return false;
      }

      return true;
    } catch (err) {
      return false;
    }
  };

  return {
    shouldShowOnboardingForm,
    markOnboardingCompleted,
    createUserProfile
  };
};